import { Children, cloneElement, isValidElement, type ComponentProps, type ReactElement } from "react";
import guide from "../../../api/src/policies/domain/policy-field-guide.json";
import { Field, Input, Select, Textarea } from "./ui/field";

export const policyFieldGuide = guide;
export type PolicyFieldKey = keyof typeof guide.fields;

export function fieldKey(id: string): PolicyFieldKey | undefined {
  if (id === "should_match" || id === "should_not_match") return `eval_cases.${id}`;
  if (/^source-kind-\d+$/.test(id)) return "sources[].kind";
  if (/^source-ref-\d+$/.test(id)) return "sources[].ref";
  if (/^available-ref-\d+$/.test(id)) return "available[].ref";
  if (/^available-when-\d+$/.test(id)) return "available[].when";
  return Object.hasOwn(guide.fields, id) ? id as PolicyFieldKey : undefined;
}

export function PolicyFieldHelp({ field, id }: { field: PolicyFieldKey; id?: string }) {
  const entry = guide.fields[field];
  return <div className="text-muted flex flex-col gap-1.5 text-xs">
    <p id={id}><span className="font-medium">{entry.requirement}. </span>{entry.meaning}</p>
    <details>
      <summary className="cursor-pointer">Formato e exemplo</summary>
      <div className="mt-2 flex flex-col gap-1.5">
        <p>{entry.format}</p>
        <p>Exemplo sintético: <code className="break-words">{typeof entry.example === "string" ? entry.example : JSON.stringify(entry.example)}</code></p>
        <p>{entry.avoid}</p>
      </div>
    </details>
  </div>;
}

export function PolicyField({ htmlFor, children, hint, ...props }: ComponentProps<typeof Field>) {
  const key = fieldKey(htmlFor);
  if (!key) return <Field htmlFor={htmlFor} hint={hint} {...props}>{children}</Field>;
  const helpId = `${htmlFor}-guidance`;
  const required = ["policy_id", "domain", "title", "situation", "rule", "customer_text", "change_reason", "should_match", "should_not_match"].includes(htmlFor);
  const describedChildren = Children.map(children, (child) => {
    if (!isValidElement(child) || ![Input, Select, Textarea].includes(child.type as typeof Input)) return child;
    const control = child as ReactElement<{ "aria-describedby"?: string; "aria-required"?: boolean; placeholder?: string }>;
    return cloneElement(control, { "aria-required": required || undefined, placeholder: control.props.placeholder ?? (typeof guide.fields[key].example === "string" && control.type !== Select ? guide.fields[key].example as string : undefined), "aria-describedby": [control.props["aria-describedby"], helpId].filter(Boolean).join(" ") });
  });
  return <Field htmlFor={htmlFor} {...props} hint={<>{hint ? <p>{hint}</p> : null}<PolicyFieldHelp field={key} id={helpId} /></>}>
    {describedChildren}
  </Field>;
}
